/**
 * Edge Commerce Protocol Engine - Accessibility Utilities
 * Comprehensive accessibility testing and validation tools
 */

// Accessibility color contrast checker
export function checkContrast(
  foreground: string,
  background: string,
  level: 'AA' | 'AAA' = 'AA',
  size: 'normal' | 'large' = 'normal'
): { ratio: number; passes: boolean } {
  // Convert hex to RGB
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgb1 = hexToRgb(foreground);
  const rgb2 = hexToRgb(background);
  
  if (!rgb1 || !rgb2) {
    return { ratio: 0, passes: false };
  }

  // Calculate relative luminance
  const getLuminance = (r: number, g: number, b: number) => {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);
  
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  
  const ratio = (brightest + 0.05) / (darkest + 0.05);
  
  // WCAG requirements
  const requirements = {
    AA: { normal: 4.5, large: 3.0 },
    AAA: { normal: 7.0, large: 4.5 }
  };
  
  const required = requirements[level][size];
  const passes = ratio >= required;
  
  return { ratio, passes };
}

// Focus management utilities
export class FocusManager {
  private focusableElements: string[] = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]'
  ];

  getFocusableElements(container: Element): HTMLElement[] {
    const selector = this.focusableElements.join(', ');
    return Array.from(container.querySelectorAll(selector))
      .filter(el => el instanceof HTMLElement)
      .filter(el => {
        // Check if element is visible and not disabled
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && 
               style.visibility !== 'hidden' && 
               !el.hasAttribute('disabled') &&
               !el.getAttribute('aria-hidden');
      });
  }

  trapFocus(container: Element): () => void {
    const focusableElements = this.getFocusableElements(container);
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e: Event) => {
      const keyboardEvent = e as KeyboardEvent;
      if (keyboardEvent.key !== 'Tab') return;

      if (keyboardEvent.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          keyboardEvent.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          keyboardEvent.preventDefault();
        }
      }
    };

    container.addEventListener('keydown', handleTabKey);
    
    // Focus first element
    if (firstElement) {
      firstElement.focus();
    }

    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleTabKey);
    };
  }

  restoreFocus(element: Element): void {
    if (element instanceof HTMLElement) {
      element.focus();
    }
  }
}

// Screen reader announcements
export class ScreenReader {
  private static createLiveRegion(): HTMLElement {
    const region = document.createElement('div');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.className = 'sr-only';
    Object.assign(region.style, {
      position: 'absolute',
      left: '-10000px',
      width: '1px',
      height: '1px',
      overflow: 'hidden'
    });
    document.body.appendChild(region);
    return region;
  }

  private static liveRegion: HTMLElement | null = null;

  static announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (!this.liveRegion) {
      this.liveRegion = this.createLiveRegion();
    }

    this.liveRegion.setAttribute('aria-live', priority);
    this.liveRegion.textContent = message;

    // Clear after announcement
    setTimeout(() => {
      if (this.liveRegion) {
        this.liveRegion.textContent = '';
      }
    }, 1000);
  }
}

// Keyboard navigation utilities
export function handleKeyboardNavigation(
  element: HTMLElement,
  options: {
    onEnter?: () => void;
    onSpace?: () => void;
    onEscape?: () => void;
    onArrowKeys?: (direction: 'up' | 'down' | 'left' | 'right') => void;
  } = {}
): () => void {
  const handleKeyDown = (e: Event) => {
    const keyboardEvent = e as KeyboardEvent;
    switch (keyboardEvent.key) {
      case 'Enter':
        keyboardEvent.preventDefault();
        options.onEnter?.();
        break;
      case ' ':
        keyboardEvent.preventDefault();
        options.onSpace?.();
        break;
      case 'Escape':
        keyboardEvent.preventDefault();
        options.onEscape?.();
        break;
      case 'ArrowUp':
        keyboardEvent.preventDefault();
        options.onArrowKeys?.('up');
        break;
      case 'ArrowDown':
        keyboardEvent.preventDefault();
        options.onArrowKeys?.('down');
        break;
      case 'ArrowLeft':
        keyboardEvent.preventDefault();
        options.onArrowKeys?.('left');
        break;
      case 'ArrowRight':
        keyboardEvent.preventDefault();
        options.onArrowKeys?.('right');
        break;
    }
  };

  element.addEventListener('keydown', handleKeyDown);
  
  return () => {
    element.removeEventListener('keydown', handleKeyDown);
  };
}

// ARIA attribute utilities
export function setAriaAttributes(element: HTMLElement, attributes: Record<string, string>): void {
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

// Skip links utility
export function createSkipLinks(): HTMLElement {
  const skipLinks = document.createElement('div');
  skipLinks.className = 'skip-links';
  
  const skipToMain = document.createElement('a');
  skipToMain.href = '#main-content';
  skipToMain.textContent = 'Skip to main content';
  skipToMain.className = 'skip-to-main';
  
  skipLinks.appendChild(skipToMain);
  return skipLinks;
}

// Color blindness simulator
export function simulateColorBlindness(type: 'protanopia' | 'deuteranopia' | 'tritanopia'): void {
  const filters = {
    protanopia: 'url(#protanopia-filter)',
    deuteranopia: 'url(#deuteranopia-filter)',
    tritanopia: 'url(#tritanopia-filter)'
  };
  
  // Create SVG filters if they don't exist
  if (!document.getElementById('colorblind-filters')) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.id = 'colorblind-filters';
    svg.style.display = 'none';
    
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    
    // Protanopia filter (red-blind)
    const protanopiaFilter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    protanopiaFilter.id = 'protanopia-filter';
    protanopiaFilter.innerHTML = `
      <feColorMatrix type="matrix" values="
        0.567, 0.433, 0,     0, 0
        0.558, 0.442, 0,     0, 0
        0,     0.242, 0.758, 0, 0
        0,     0,     0,     1, 0
      "/>
    `;
    
    // Deuteranopia filter (green-blind)
    const deuteranopiaFilter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    deuteranopiaFilter.id = 'deuteranopia-filter';
    deuteranopiaFilter.innerHTML = `
      <feColorMatrix type="matrix" values="
        0.625, 0.375, 0,   0, 0
        0.7,   0.3,   0,   0, 0
        0,     0.3,   0.7, 0, 0
        0,     0,     0,   1, 0
      "/>
    `;
    
    // Tritanopia filter (blue-blind)
    const tritanopiaFilter = document.createElementNS('http://www.w3.org/2000/svg', 'filter');
    tritanopiaFilter.id = 'tritanopia-filter';
    tritanopiaFilter.innerHTML = `
      <feColorMatrix type="matrix" values="
        0.95, 0.05,  0,     0, 0
        0,    0.433, 0.567, 0, 0
        0,    0.475, 0.525, 0, 0
        0,    0,     0,     1, 0
      "/>
    `;
    
    defs.appendChild(protanopiaFilter);
    defs.appendChild(deuteranopiaFilter);
    defs.appendChild(tritanopiaFilter);
    svg.appendChild(defs);
    document.body.appendChild(svg);
  }
  
  // Apply filter to body
  document.body.style.filter = filters[type];
}

export function clearColorBlindnessSimulation(): void {
  document.body.style.filter = '';
}

// Reduced motion detection
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// High contrast mode detection
export function prefersHighContrast(): boolean {
  return window.matchMedia('(prefers-contrast: high)').matches;
}

// Initialize accessibility features
export function initializeAccessibility(): void {
  // Add skip links
  const skipLinks = createSkipLinks();
  document.body.insertBefore(skipLinks, document.body.firstChild);
  
  // Add screen reader styles
  const style = document.createElement('style');
  style.textContent = `
    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
    
    .skip-to-main:focus {
      position: absolute !important;
      width: auto !important;
      height: auto !important;
      padding: 0.5rem 1rem !important;
      margin: 0.5rem !important;
      overflow: visible !important;
      clip: auto !important;
      white-space: normal !important;
      background: var(--color-primary-600) !important;
      color: var(--color-text-on-primary) !important;
      border-radius: var(--radius-md) !important;
      z-index: var(--z-tooltip) !important;
    }
    
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
        scroll-behavior: auto !important;
      }
    }
  `;
  document.head.appendChild(style);
  
  // Announce page load to screen readers
  setTimeout(() => {
    ScreenReader.announce('Page loaded');
  }, 1000);
}
